import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'

import { cn } from '../cn'

// Card — the product's default container (design/components/card.html). Thin
// border, 13px corner, layered subtle shadow. Composes Head / Body / Section /
// Foot. Emphasis lives in the BORDER (accent/danger), never a loud fill.
// `hoverable` only when the whole card is a link — pass `href` and it renders
// a real <a> with a visible focus ring (no onClick-on-a-div).

const cardVariants = cva('rounded-lg border bg-surface', {
  variants: {
    variant: {
      base: 'border-line',
      accent: 'border-[color-mix(in_srgb,var(--brand-blue)_40%,transparent)]',
      sens: 'border-[color-mix(in_srgb,var(--sens)_40%,transparent)] bg-sens-bg',
    },
    flat: { true: 'shadow-none', false: 'shadow-card' },
    hoverable: {
      true: 'transition-[box-shadow,transform] duration-normal ease-vysto hover:-translate-y-[3px] hover:shadow-card-hover',
      false: '',
    },
  },
  defaultVariants: { variant: 'base', flat: false, hoverable: false },
})

type CardOwnProps = VariantProps<typeof cardVariants> & {
  /** When set the whole card is a link: renders <a> with a focus ring + lift. */
  href?: string
  children: ReactNode
  className?: string
}

export type CardProps = CardOwnProps & Omit<HTMLAttributes<HTMLElement>, keyof CardOwnProps>

/** Default product container (exported as `Card` with Head/Body/Section/Foot
 *  attached). Emphasis lives in the border variant, never a loud fill; `href`
 *  makes the whole card a real <a> with focus ring + hover lift — no
 *  onClick-on-a-div. */
export const CardRoot = forwardRef<HTMLElement, CardProps>(function Card(
  { variant, flat, hoverable, href, className, children, ...rest },
  ref,
) {
  const classes = cn(
    cardVariants({ variant, flat, hoverable: hoverable ?? Boolean(href) }),
    href &&
      'block focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue',
    className,
  )
  if (href) {
    return (
      <a ref={ref as React.Ref<HTMLAnchorElement>} href={href} className={classes} {...rest}>
        {children}
      </a>
    )
  }
  return (
    <section ref={ref} className={classes} {...rest}>
      {children}
    </section>
  )
})
CardRoot.displayName = 'Card'

export interface CardHeadProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode
  sub?: ReactNode
  action?: ReactNode
  /** Heading level for the title (default 3). */
  headingLevel?: 2 | 3 | 4
}

/** Bordered card header: title (h2–h4 via `headingLevel`) + sub + trailing
 *  `action` slot; passing `children` replaces the title/sub block entirely. */
export function CardHead({
  title,
  sub,
  action,
  headingLevel = 3,
  className,
  children,
  ...rest
}: CardHeadProps) {
  const Heading = `h${headingLevel}` as const
  return (
    <div
      className={cn(
        'flex items-center gap-2.5 border-b border-line px-[18px] py-[15px]',
        className,
      )}
      {...rest}
    >
      {children ?? (
        <div className="min-w-0">
          {title && (
            <Heading className="truncate text-[13.5px] font-bold text-text">{title}</Heading>
          )}
          {sub && <div className="mt-px text-[11px] text-text-3">{sub}</div>}
        </div>
      )}
      {action && <div className="ml-auto flex flex-none items-center gap-2">{action}</div>}
    </div>
  )
}

/** Padded card content area; use CardSection instead when stacking ruled
 *  regions. */
export function CardBody({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-[18px] py-4', className)} {...rest} />
}

export function CardSection({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('border-t border-line px-[18px] py-4 first:border-t-0', className)}
      {...rest}
    />
  )
}

/** Tinted bottom bar of a Card for actions/meta; must be the last child so
 *  its rounded bottom matches the card's radius. */
export function CardFoot({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex items-center gap-[9px] rounded-b-lg border-t border-line bg-surface-2 px-[18px] py-[13px]',
        className,
      )}
      {...rest}
    />
  )
}

export const Card = Object.assign(CardRoot, {
  Head: CardHead,
  Body: CardBody,
  Section: CardSection,
  Foot: CardFoot,
})
